import { formatCurrency } from '../../components/util/Currency';
import {
  Carrier,
  CarrierType,
  PLAIN_LABEL_CARRIER_TYPE,
} from '../../types/carrier';
import { DropoffLocation } from '../../types/dropoff-location';

export const EparcelDropOffItem: MethodProps = {
  title: 'Australia Post Drop Off',
  cost: '',
  description:
    'Drop off your parcel in a post box or at any Australia Post office location',
  dropoff: true,
};

export const PickUpItem: MethodProps = {
  title: 'Courier Pickup',
  cost: '',
  description:
    'Book a courier to collect your parcel from your pickup address.',
  dropoff: false,
};

export const ReturnInStoreItem: MethodProps = {
  title: 'Return in store',
  cost: '',
  description: 'Return your item in store during store hours at any store.',
  dropoff: true,
};

export const SelfReturnItem: MethodProps = {
  title: 'Arrange your own courier',
  cost: '',
  description:
    'Your address is outside the serviceable area of our return couriers. Use a courier that provides tracking to return your order.',
  dropoff: true,
};

export interface MethodProps {
  title: string;
  cost: string;
  description: string;
  dropoff: boolean;
}

class Method {
  public title: string;

  private cost: string;

  public description: string;

  private dropoff: boolean;

  public index: number;

  private carrierName: string | null;

  private carrierType: string | null;

  private carriers: Carrier[] | null;

  public poBoxError: boolean | undefined | null;

  private returnCharge: string | undefined;

  private currencyCode: string | undefined;

  private dropoffLocation: DropoffLocation | undefined;

  constructor(
    baseProps: MethodProps,
    position: number,
    carriers: Carrier | Carrier[] | null,
    returnCharge?: string,
    currencyCode?: string
  ) {
    this.title = baseProps.title;
    this.cost = baseProps.cost;
    this.description = baseProps.description;
    this.dropoff = baseProps.dropoff;
    this.carriers =
      carriers && !Array.isArray(carriers) ? [carriers] : carriers;
    this.index = position === -1 ? 100 : position;
    this.carrierName = this.carriers?.length
      ? this.carriers[0].carrier_name
      : null;
    this.carrierType = this.carriers?.length
      ? this.carriers[0].carrier_type
      : null;
    this.poBoxError = undefined;
    this.returnCharge = returnCharge;
    this.currencyCode = currencyCode;
    this.dropoffLocation = undefined;
  }

  isAvailable() {
    return this.index !== 100;
  }

  isDropOff() {
    return this.dropoff;
  }

  isEparcel() {
    return this.carrierType === 'Eparcel' && this.isDropOff();
  }

  isHubbed() {
    return this.carrierType === 'Hubbed' && this.isDropOff();
  }

  isReturnInStore() {
    return (
      this.carrierType === PLAIN_LABEL_CARRIER_TYPE &&
      this.isDropOff() &&
      this.carrierName === 'Return in store'
    );
  }

  isSelfReturn() {
    return (
      this.carrierType === PLAIN_LABEL_CARRIER_TYPE &&
      this.isDropOff() &&
      this.carrierName === 'Self Return'
    );
  }

  showDropOff() {
    return this.getSupportDropOffCarriers().length > 0 && this.isDropOff();
  }

  getSupportDropOffCarriers(): CarrierType[] {
    return [];
  }

  getName() {
    return this.carrierName;
  }

  getType() {
    return this.carrierType!;
  }

  setType(type: CarrierType) {
    this.carrierType = type;
  }

  getReturnCharge() {
    if (this.isReturnInStore() || this.isSelfReturn()) {
      return formatCurrency(0, this.currencyCode || 'AUD');
    }

    return this.returnCharge;
  }

  setDropoffLocation(dropoffLocation: DropoffLocation) {
    this.dropoffLocation = dropoffLocation;
  }

  getDropoffLocation(): DropoffLocation | undefined {
    return this.dropoffLocation!;
  }
}

export default Method;
